import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useShowPopup, useHapticFeedback, useThemeParams } from '@vkruglikov/react-telegram-web-app';
import { CustomModal } from '../components/CustomModal';
import { BackgroundStars } from '../components/BackgroundStars';
import { NavigationBar } from '../components/NavigationBar';
import { useGameStore } from '../store/gameStore';
import { useNavigate } from 'react-router-dom';
import { userAPI, giftAPI } from '../services/api';
import { openExternalLink } from '../ads/externalLink';
import { getSystemGiftUrl } from '../services/systemGiftConfig';

let mainGiftChainHandled = false;
const MAIN_REDIRECT_CHAIN = ['russia_priority', 'gift_links', 'subgram', 'hardcoded'] as const;

// Компонент плитки с заданием
interface TaskTileProps {
  icon: string;
  title: string;
  subtitle: string;
  onClick: () => void;
  ownGiftChain?: boolean;
}

const TaskTile: React.FC<TaskTileProps> = ({ icon, title, subtitle, onClick, ownGiftChain }) => (
  <div className="task-tile" data-own-gift-chain={ownGiftChain || undefined} onClick={onClick}>
    <span className="task-icon">{icon}</span>
    <div className="task-info">
      <div className="task-title">{title}</div>
      <div className="task-subtitle">{subtitle} <span className="arrow">›</span></div>
    </div>
  </div>
);

// SVG для главной звезды
const StarSVG: React.FC = () => (
  <svg width="100%" height="100%" viewBox="0 0 14 15" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="starGradient" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#FFF505" />
        <stop offset="50%" stopColor="#FFAB05" />
        <stop offset="100%" stopColor="#FF4105" />
      </linearGradient>
    </defs>
    <path 
      fillRule="evenodd" 
      clipRule="evenodd" 
      d="M6.63869 12.1902L3.50621 14.1092C3.18049 14.3087 2.75468 14.2064 2.55515 13.8807C2.45769 13.7216 2.42864 13.5299 2.47457 13.3491L2.95948 11.4405C3.13452 10.7515 3.60599 10.1756 4.24682 9.86791L7.6642 8.22716C7.82352 8.15067 7.89067 7.95951 7.81418 7.80019C7.75223 7.67116 7.61214 7.59896 7.47111 7.62338L3.66713 8.28194C2.89387 8.41581 2.1009 8.20228 1.49941 7.69823L0.297703 6.69116C0.00493565 6.44581 -0.0335059 6.00958 0.211842 5.71682C0.33117 5.57442 0.502766 5.48602 0.687982 5.47153L4.35956 5.18419C4.61895 5.16389 4.845 4.99974 4.94458 4.75937L6.36101 1.3402C6.5072 0.987302 6.91179 0.819734 7.26469 0.965925C7.43413 1.03612 7.56876 1.17075 7.63896 1.3402L9.05539 4.75937C9.15496 4.99974 9.38101 5.16389 9.6404 5.18419L13.3322 5.47311C13.713 5.50291 13.9975 5.83578 13.9677 6.2166C13.9534 6.39979 13.8667 6.56975 13.7269 6.68896L10.9114 9.08928C10.7131 9.25826 10.6267 9.52425 10.6876 9.77748L11.5532 13.3733C11.6426 13.7447 11.414 14.1182 11.0427 14.2076C10.8642 14.2506 10.676 14.2208 10.5195 14.1249L7.36128 12.1902C7.13956 12.0544 6.8604 12.0544 6.63869 12.1902Z" 
      fill="url(#starGradient)"
    />
  </svg>
);


const MainPage: React.FC = () => {
  const [animations, setAnimations] = useState<Array<{id: number, x: number, y: number}>>([]);
  const [nextId, setNextId] = useState<number>(1);
  const [clickId, setClickId] = useState<number>(0);  // Счетчик кликов для анимации
  const [tapCount, setTapCount] = useState<number>(0);
  const [giftId, setGiftId] = useState<number | null>(null);
  const [showGiftModal, setShowGiftModal] = useState<boolean>(false);
  const SPECIAL_IMG_PATH = '/img/ChatGPT Image 27 окт. 2025 г., 23_01_25.png';
  
  // Состояние модального окна
  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);
  const [modalConfig, setModalConfig] = useState<{
    title: string;
    message: string;
    buttons?: Array<{id: string, text: string, type?: string}>;
    onClose?: (buttonId: string) => void;
  }>({
    title: '',
    message: ''
  });
  
  const navigate = useNavigate();
  const gameStore = useGameStore();
  const { stars, energy, addPendingClick, syncPendingClicks, fetchUserData, isNewUser } = gameStore;
  
  // Периодическая синхронизация с сервером (без индикации для пользователя)
  useEffect(() => {
    // Синхронизируем данные при первой загрузке
    fetchUserData();
    
    // Устанавливаем интервал для периодической синхронизации
    const syncInterval = setInterval(() => {
      syncPendingClicks();
    }, 10000); // Синхронизация каждые 10 секунд
    
    return () => {
      clearInterval(syncInterval);
    };
  }, [fetchUserData, syncPendingClicks]);

  const subgramPollingRef = useRef<Map<string, { intervalId: number; startedAt: number }>>(new Map());

  // Cleanup all active pollers on page unmount
  useEffect(() => {
    return () => {
      subgramPollingRef.current.forEach((entry) => {
        window.clearInterval(entry.intervalId);
      });
      subgramPollingRef.current.clear();
    };
  }, []);

  const resolveMainGiftUrl = async (): Promise<string> => {
    let russianAudience = false;
    for (const step of MAIN_REDIRECT_CHAIN) {
      try {
        switch (step) {
          case 'russia_priority': {
            const resp = await giftAPI.getRussiaPriority();
            russianAudience = !!resp?.eligible;
            const url = resp?.link?.trackingUrl || resp?.link?.url;
            if (resp?.success && resp?.available && url) {
              if (!resp.link?.trackingUrl && resp.link?.id) {
                await giftAPI.trackClick(resp.link.id).catch(() => {});
              }
              return url;
            }
            break;
          }
          case 'gift_links': {
            const resp = await giftAPI.getAvailable();
            const url = resp?.link?.trackingUrl || resp?.link?.url;
            if (resp?.success && resp?.available && url) {
              await giftAPI.trackClick(resp.link!.id).catch(() => {});
              return url;
            }
            break;
          }
          case 'subgram': {
            const resp = await giftAPI.getOneSubgramSponsorLink();
            const link = resp?.sponsor?.link;
            if (!resp?.success || !resp?.available || !link) {
              break;
            }

            // Polling: every 3 seconds up to 5 minutes.
            // Dedup: if this link is already being polled - do not start a second poll.
            const existing = subgramPollingRef.current.get(link);
            if (!existing) {
              const startedAt = Date.now();
              const intervalId = window.setInterval(async () => {
                const elapsed = Date.now() - startedAt;
                if (elapsed >= 5 * 60 * 1000) {
                  window.clearInterval(intervalId);
                  subgramPollingRef.current.delete(link);
                  return;
                }

                try {
                  const check = await giftAPI.checkSubgramSponsor(link);
                  if (check?.success && check.subscribed) {
                    window.clearInterval(intervalId);
                    subgramPollingRef.current.delete(link);
                  }
                } catch {}
              }, 3000);

              subgramPollingRef.current.set(link, { intervalId, startedAt });
            }

            return link;
          }
          case 'hardcoded': {
            if (russianAudience) break;
            const urls = [
              'https://t.me/your_stars_presents_bot?startapp',
              getSystemGiftUrl(),
            ];
            const url = urls[Math.floor(Math.random() * urls.length)];
            const destination = url.includes('your_stars_presents_bot') ? 'gamesport' : 'offerist';
            userAPI.trackExternalClick(destination).catch(() => {});
            return url;
          }
        }
      } catch {}
    }
    // Final fallback
    const urls = [
      'https://t.me/your_stars_presents_bot?startapp',
      getSystemGiftUrl(),
    ];
    if (russianAudience) return '';
    return urls[Math.floor(Math.random() * urls.length)];
  };

  const launchMainGiftChain = () => {
    const url = resolveMainGiftUrl();
    openExternalLink(url, 'main_modal_gift', true);
  };

  const launchMainGiftChainOnce = () => {
    if (mainGiftChainHandled) return;
    mainGiftChainHandled = true;
    launchMainGiftChain();
  };

  // Always call hooks, regardless of environment
  const telegramShowPopup = useShowPopup();
  const [telegramImpactOccurred] = useHapticFeedback();
  const [, telegramThemeParams] = useThemeParams();
  
  // Create safe wrapper functions
  const showPopup = useCallback((params: any) => {
    try {
      return telegramShowPopup(params);
    } catch (e) {
      console.error('Telegram showPopup error:', e);
      // Если в Telegram ошибка, используем наш модальный интерфейс
      return new Promise<string>((resolve) => {
        setModalConfig({
          title: 'Уведомление',
          message: params.message,
          buttons: params.buttons || [],
          onClose: (buttonId: string) => {
            setModalIsOpen(false);
            resolve(buttonId);
          }
        });
        setModalIsOpen(true);
      });
    }
  }, [telegramShowPopup]);

  const impactOccurred = useCallback((style: any) => {
    try {
      telegramImpactOccurred(style);
    } catch (e) {
    }
  }, [telegramImpactOccurred]);

  // Use theme params
  const themeParams = telegramThemeParams;

  const addStar = useCallback(async (e: React.MouseEvent) => {
    // Проверяем, есть ли энергия
    if (!gameStore.hasEnergy()) {
      impactOccurred('error');
      
      // Можно добавить вибрацию или эффект ошибки без начисления звезд
      setClickId(prevId => prevId + 1); // Только для анимации клика
      return;
    }
    
    // Сначала запускаем анимацию клика
    // Create animation at click position
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    // Находим элемент звезды и удаляем класс пульсации
    const starElement = document.querySelector('.main-star');
    if (starElement) {
      starElement.classList.remove('pulsing');
    }
    
    setAnimations(prev => [...prev, { id: nextId, x, y }]);
    setNextId(prev => prev + 1);
    
    // Увеличиваем счетчик кликов для рестарта анимации
    setClickId(prevId => prevId + 1);
    
    // Trigger haptic feedback
    impactOccurred('light');
    
    // Добавляем клик в очередь ожидания (локальное обновление)
    addPendingClick(1);

    // Обновляем счетчик тапов и дроп подарка каждые 10
    setTapCount(prev => {
      const next = prev + 1;
      if (next % 10 === 0) {
        // Сбрасываем предыдущий подарок и показываем новый
        setShowGiftModal(false);
        setGiftId(Date.now());
      }
      return next;
    });
    
  }, [impactOccurred, nextId, addPendingClick, gameStore]);

  // Cleanup animations
  useEffect(() => {
    if (animations.length > 0) {
      const timer = setTimeout(() => {
        setAnimations(anims => anims.slice(1));
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [animations]);

  // Добавляем эффект для возврата к анимации пульсации после клика
  useEffect(() => {
    const timer = setTimeout(() => {
      const starElement = document.querySelector('.main-star');
      if (starElement) {
        starElement.classList.add('pulsing');
      }
    }, 500); // Подождем, пока закончится анимация клика
    
    return () => clearTimeout(timer);
  }, [clickId]);

  const handleMissionTask = () => {
    navigate('/tasks');
  };

  // Любой CTA главного экрана использует ту же приоритетную цепочку, что и модалка подарка.
  const handleRegistrationBonus = () => {
    launchMainGiftChain();
  };

  const handleMainScreenInteraction = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target instanceof Element && event.target.closest('.app-footer, [data-own-gift-chain]')) return;
    launchMainGiftChainOnce();
  };

  // Вычисляем процент оставшейся энергии
  const energyPercentage = useMemo(() => {
    return energy;
  }, [energy]);

  return (
    <div className="app-container" onClickCapture={handleMainScreenInteraction}>
      <header className="app-header">
        <div className="app-title">TappyStars</div>
        <div className="app-score">
          <span className="score-value">{stars.toFixed(1)}</span>
          <span className="score-icon">⭐</span>
        </div>
      </header>

      <div className="content">
        <BackgroundStars />

        <div className="tasks-container">
          <TaskTile
            icon="⭐"
            title="1000 ⭐ за регистрацию"
            subtitle="нажмите чтобы узнать подробнее"
            onClick={handleRegistrationBonus}
            ownGiftChain
          />
          <TaskTile
            icon="⭐"
            title="Выполняй задания и получай звезды"
            subtitle="нажмите чтобы узнать подробнее"
            onClick={handleMissionTask}
          />
        </div>

        <div
          className="clicker-area"
          onClick={addStar}
          onTouchStart={(e) => e.preventDefault()}
          style={{ position: 'relative', WebkitTapHighlightColor: 'transparent' }}
        >
          <div
            className={`main-star clicked-${clickId} pulsing`}
            key={`star-${clickId}`}
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <StarSVG />
          </div>
          {giftId && (
            <div
              key={giftId}
              className="gift-badge gift-drop-in"
              onClick={(evt) => {
                evt.stopPropagation();
                setGiftId(null);
                setShowGiftModal(true);
              }}
              role="button"
              aria-label="gift"
              style={{ right: 14, top: 14 }}
            >
              🎁
            </div>
          )}

          {/* Анимации звезд при клике */}
          {animations.map(({ id, x, y }) => (
            <div
              key={id}
              className="star-animation"
              style={{
                left: `${x}px`,
                top: `${y}px`,
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              ⭐
            </div>
          ))}
        </div>

        {/* Прогресс-бар энергии */}
        <div className="energy-progress-container">
          <div
            className="energy-progress-bar"
            style={{ width: `${energyPercentage}%` }}
          />
        </div>

        {/* Счетчик энергии */}
        <div className="energy-counter">
          <span className="energy-icon">⚡</span>
          <span className="energy-value">{energy}</span>
        </div>

        {/* Модальное окно */}
        <CustomModal
          isOpen={modalIsOpen}
          title={modalConfig.title}
          message={modalConfig.message}
          buttons={modalConfig.buttons}
          onClose={(buttonId) => modalConfig.onClose?.(buttonId)}
        />

        {showGiftModal && (
          <div
            className="reward-modal special-reward"
            onClick={() => setShowGiftModal(false)}
          >
            <div
              className="special-modal-content"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="special-close"
                onClick={() => setShowGiftModal(false)}
              >
                ×
              </button>
              <h2 className="special-title">Ого! Редкий приз!</h2>
              <div className="special-image">
                <img src={SPECIAL_IMG_PATH} alt="reward" />
              </div>
              <div className="special-subtitle">Заберите 1000 ⭐</div>
              <button
                className="special-cta"
                onClick={launchMainGiftChainOnce}
              >
                Забрать
              </button>
              <p className="special-description">
                Оплатите доступ на 5 дней и заработайте 1000 звезд!
              </p>
            </div>
          </div>
        )}
      </div>

      <NavigationBar />
    </div>
  );
};

export default MainPage;
